/**
 * How a live order reads on screen — a 1:1 port of Android's `OrderTrackingPresentation.kt`.
 *
 * **The client does not re-derive this mapping.** Both Android and iOS got it wrong by inventing
 * their own classification, which is why the plan asks for a literal port. Everything here is a
 * lookup: the server names a `tracking_substate`, this decides which of six steps it lands on and
 * what, if anything, is said above it.
 *
 * **An unknown substate renders, it does not crash.** The backend gains states faster than clients
 * ship, and a screen that throws on one nobody has heard of is worse than a screen showing a raw
 * string. This is the one place a catch-all branch is correct.
 */
import { formatMoney } from '@/shared/lib/format';

/** The six visual steps, in order. */
export const STEP_LABELS = [
  'Ищем оператора',
  'Назначен',
  'В пути',
  'На месте',
  'Заливает',
  'Завершено',
] as const;

const STEP_SEARCHING = 0;
const STEP_ASSIGNED = 1;
const STEP_EN_ROUTE = 2;
const STEP_ARRIVED = 3;
const STEP_REFILLING = 4;
const STEP_COMPLETED = 5;

export type TrackingScreenState =
  | {
      kind: 'stepper';
      stepIndex: number;
      /** Informational, non-blocking text for a substate that shares its parent step. */
      banner: string | null;
      /** `COMPLETING` only — the last step shown as in-progress rather than done. */
      isFinalizing: boolean;
    }
  /** `CANCELED` — deliberately not on the stepper; it gets its own terminal card. */
  | { kind: 'canceled' }
  /** A substate this build has never heard of. Rendered raw, neutrally. */
  | { kind: 'unknown'; rawSubstate: string };

const SEARCHING_SLOW_BANNER =
  'Поиск занимает больше обычного — вы можете бесплатно отменить заявку';
const REASSIGNING_BANNER = 'Оператор сменился — ищем нового';
const DISPATCHER_REVIEW_BANNER =
  'Уточняем детали заезда. Оператор дальше 150 м от точки заказа или GPS недоступен — ' +
  'заявку на секунду смотрит диспетчер, это нормально, скоро продолжим.';

function stepper(stepIndex: number, banner: string | null = null, isFinalizing = false): TrackingScreenState {
  return { kind: 'stepper', stepIndex, banner, isFinalizing };
}

export function classify(trackingSubstate: string): TrackingScreenState {
  switch (trackingSubstate) {
    case 'SEARCHING':
      return stepper(STEP_SEARCHING);
    case 'SEARCHING_SLOW':
      return stepper(STEP_SEARCHING, SEARCHING_SLOW_BANNER);
    case 'REASSIGNING':
      return stepper(STEP_SEARCHING, REASSIGNING_BANNER);
    case 'ASSIGNED':
      return stepper(STEP_ASSIGNED);
    case 'EN_ROUTE':
      return stepper(STEP_EN_ROUTE);
    case 'DISPATCHER_REVIEW_START':
      return stepper(STEP_EN_ROUTE, DISPATCHER_REVIEW_BANNER);
    case 'ARRIVED':
      return stepper(STEP_ARRIVED);
    case 'REFILLING':
      return stepper(STEP_REFILLING);
    case 'DISPATCHER_REVIEW_COMPLETION':
      return stepper(STEP_REFILLING, DISPATCHER_REVIEW_BANNER);
    case 'COMPLETING':
      return stepper(STEP_COMPLETED, null, true);
    case 'COMPLETED':
      return stepper(STEP_COMPLETED);
    case 'CANCELED':
      return { kind: 'canceled' };
    default:
      return { kind: 'unknown', rawSubstate: trackingSubstate };
  }
}

/**
 * The live pin only makes sense while the truck is moving.
 *
 * After `ARRIVED` it is parked at the car, and a map tracking a stationary vehicle is an
 * invitation to keep staring at nothing.
 */
export function showsLiveOperatorPin(trackingSubstate: string): boolean {
  return trackingSubstate === 'EN_ROUTE';
}

/**
 * What stands in for the map when there is no pin to show.
 *
 * The placeholder used to promise «карта появится, когда оператор будет в пути» in *every* such
 * case — including `ARRIVED` and `REFILLING`, where the operator is already at the car and no map
 * is ever coming, leaving somebody waiting for something that would never appear (audit
 * 2026-08-18).
 */
/**
 * Стоит ли вообще держать на экране место под карту.
 *
 * Пока заправщик едет — да: карта либо уже там, либо вот-вот появится, и пустая рамка объясняет,
 * чего ждать. Как только он приехал, объяснять нечего: рамка с текстом «следить по карте больше
 * нечего» сообщает ровно то, что и так видно по шкале, и занимает экран ради этого. Владелец
 * 2026-08-28, про блок на ARRIVED: «нахуя тот блок?».
 *
 * Дальше по заказу — заправка, завершение — тем более: там на экране деньги и литры, а не дорога.
 */
export function expectsMap(trackingSubstate: string): boolean {
  return (
    trackingSubstate === 'SEARCHING' ||
    trackingSubstate === 'SEARCHING_SLOW' ||
    trackingSubstate === 'REASSIGNING' ||
    trackingSubstate === 'ASSIGNED' ||
    trackingSubstate === 'DISPATCHER_REVIEW_START' ||
    trackingSubstate === 'EN_ROUTE'
  );
}

export function mapPlaceholderMessage(
  trackingSubstate: string,
  hasOperatorPosition: boolean,
): string {
  switch (trackingSubstate) {
    case 'EN_ROUTE':
      return hasOperatorPosition ? 'Показываем оператора на карте' : 'Ожидаем позицию оператора…';
    case 'SEARCHING':
    case 'SEARCHING_SLOW':
    case 'REASSIGNING':
    case 'ASSIGNED':
      return 'Карта появится, когда заправщик выедет к вам';
    case 'DISPATCHER_REVIEW_START':
      return 'Выезд проверяет диспетчер — карта появится после проверки';
    case 'ARRIVED':
      return 'Заправщик уже на месте — следить по карте больше нечего';
    case 'REFILLING':
    case 'DISPATCHER_REVIEW_COMPLETION':
      return 'Идёт заправка';
    case 'COMPLETING':
      return 'Заявка завершается';
    default:
      return 'Карта сейчас недоступна';
  }
}

/**
 * The substates the backend actually lets a consumer cancel from; everything else 409s.
 *
 * An allowlist rather than something derived from the step index: `REFILLING` and
 * `DISPATCHER_REVIEW_COMPLETION` share step 4 with completely different cancelability.
 *
 * `COMPLETING` is included as a deliberate tradeoff. The backend allows cancelling a
 * `PROBLEMATIC` order, but its classifier maps that to the same `COMPLETING` substate as ordinary
 * finalizing, and the client cannot tell them apart. So an order in the brief ordinary window will
 * also show a cancel button that 409s if tapped — which beats a genuinely stuck order with no
 * cancel path at all.
 */
const CANCELABLE_SUBSTATES = new Set([
  'SEARCHING',
  'SEARCHING_SLOW',
  'REASSIGNING',
  'ASSIGNED',
  'EN_ROUTE',
  'DISPATCHER_REVIEW_START',
  'ARRIVED',
  'COMPLETING',
]);

/** The backend stays the authority via its 409; this only avoids offering a button that must fail. */
export function canCancel(trackingSubstate: string): boolean {
  return CANCELABLE_SUBSTATES.has(trackingSubstate);
}

/* ---------- cars of the выезд ---------- */

export const PENDING_CAR_STATUS = 'PENDING';
export const REFUELED_CAR_STATUS = 'REFUELED';

/**
 * Whether this row still counts as a car of the выезд.
 *
 * `PENDING` and `REFUELED` do; `CANCELLED` and `SKIPPED` do not — a dropped car occupies no seat
 * in the city's cap, adds nothing to the total volume, and may be put back into the very same
 * order, which then legitimately carries two rows for one car. Anything asking «is this car
 * already in the выезд» must ask this, not «is it anywhere in `cars`».
 */
export function isCarStillInOrder(status: string): boolean {
  return status === PENDING_CAR_STATUS || status === REFUELED_CAR_STATUS;
}

export type OrderCarStatusKind =
  | 'pending'
  | 'refueled'
  /** The truck had set off before this car was dropped — the delivery for it is still owed. */
  | 'skipped'
  /** Taken off before the выезд started; nothing owed. */
  | 'dropped'
  | 'unknown';

export interface OrderCarStatus {
  kind: OrderCarStatusKind;
  label: string;
}

export function orderCarStatus(status: string): OrderCarStatus {
  switch (status) {
    case PENDING_CAR_STATUS:
      return { kind: 'pending', label: 'Ждёт' };
    case REFUELED_CAR_STATUS:
      return { kind: 'refueled', label: 'Заправлена' };
    case 'SKIPPED':
      return { kind: 'skipped', label: 'Пропущена' };
    case 'CANCELLED':
      return { kind: 'dropped', label: 'Убрана' };
    default:
      return { kind: 'unknown', label: status };
  }
}

/**
 * Whether «Убрать» may be offered for one car.
 *
 * The backend refuses to drop the last pending car («Это последняя машина в заявке — отмените
 * заявку целиком»), so a button there could only ever produce a 409. What is wanted in that case
 * is «Отменить заказ», which sits right below the list anyway.
 */
export function canDropCar(allStatuses: string[], carStatus: string): boolean {
  if (carStatus !== PENDING_CAR_STATUS) return false;
  return allStatuses.filter((s) => s === PENDING_CAR_STATUS).length > 1;
}

/**
 * Whether another car may still be added to a выезд that is already running.
 *
 * The condition is about time, not count: once the truck has arrived and fuel is going in, a late
 * addition is a second visit rather than the same one, and the backend refuses it.
 *
 * The city's cap is deliberately **not** checked here. `GET /orders/{id}` does not carry
 * `max_cars_per_order`, so the client cannot know it for a running order — guessing with the
 * wizard's default would hide the button on an order the server would happily accept. The server
 * refuses past the cap, and that refusal is shown where the car is chosen.
 */
export function canAddCarToOrder(trackingSubstate: string): boolean {
  return ADDABLE_SUBSTATES.has(trackingSubstate);
}

const ADDABLE_SUBSTATES = new Set([
  'SEARCHING',
  'SEARCHING_SLOW',
  'REASSIGNING',
  'ASSIGNED',
  'EN_ROUTE',
]);

/* ---------- money and the terminal card ---------- */

/** True only for a decimal string that parses to a strictly positive amount. */
export function isPositiveAmount(decimal: string | null | undefined): boolean {
  if (decimal === null || decimal === undefined) return false;
  const trimmed = decimal.trim();
  if (!/^[+-]?(\d+(\.\d+)?|\.\d+)$/.test(trimmed)) return false;
  return Number.parseFloat(trimmed) > 0;
}

/** The one cancelled state whose heading differs — it is not something anybody chose. */
export const NO_SHOW_STATE = 'CONSUMER_NO_SHOW';

export interface CancellationOutcome {
  title: string;
  moneyLine: string;
}

/**
 * What the terminal cancelled card says.
 *
 * The card used to read «Заказ отменён» and nothing else for all seven server states that
 * classify as cancelled (audit 2026-08-17). A free cancellation looked identical to one that took
 * the delivery fee, a dispatcher's return was invisible, and a no-show looked like the person's
 * own doing. All three facts were already in the payload; nothing was reading them.
 *
 * The refund line wins over the charge line: once money is back, that is the fact worth stating,
 * and the amount shown is what was charged, since a refund is always in full.
 */
export function cancellationOutcome(
  state: string,
  chargedAmount: string | null,
  refundedAmount: string | null,
): CancellationOutcome {
  const title =
    state === NO_SHOW_STATE ? 'Заказ отменён: не было доступа к технике' : 'Заказ отменён';

  const moneyLine = isPositiveAmount(refundedAmount)
    ? `Плата за выезд ${formatMoney(refundedAmount)} возвращена на карту`
    : isPositiveAmount(chargedAmount)
      ? `Списана плата за выезд · ${formatMoney(chargedAmount)}`
      : 'Плата не списана';

  return { title, moneyLine };
}

/* ---------- rating ---------- */

export const MIN_RATING_SCORE = 1;
export const MAX_RATING_SCORE = 5;
/** A 1 or 2 requires a comment — the backend 400s otherwise, and the same score escalates. */
export const LOW_RATING_THRESHOLD = 2;

export function ratingNeedsComment(score: number): boolean {
  return score <= LOW_RATING_THRESHOLD;
}

/** «★★★★☆». Clamped defensively — the score comes off the wire, not from this screen. */
export function starDisplay(score: number): string {
  const filled = Math.min(MAX_RATING_SCORE, Math.max(MIN_RATING_SCORE, Math.trunc(score)));
  return '★'.repeat(filled) + '☆'.repeat(MAX_RATING_SCORE - filled);
}
